import { AppIcon } from '@/components/ui/app-icon';
import { OrganizationSwitcher, useClerk } from '@clerk/react';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { PlanTierCard } from '@/components/plans/plan-tier-card.component';
import { Button } from '@/components/ui/button';
import { groupPlansByTier } from '@/constants/plan-feature-matrix';
import { PricingV4Cards } from '@/containers/settings/billing/pricing-v4-cards.component';
import type { PricingPlanSelection } from '@/containers/settings/billing/pricing-v4';
import { useFeatureAccess } from '@/hooks/use-feature-access.hook';
import { useOrganizationActionFence } from '@/hooks/use-organization-action-fence.hook';
import { usePageRestored } from '@/hooks/use-page-restored.hook';
import { bestEffortAnalytics } from '@/lib/best-effort-analytics';
import { iconSpinnerThird } from '@/lib/icons';
import { redirectToStripeCheckout } from '@/lib/stripe-redirect';
import { useAppDispatch } from '@/store';
import {
  billingApi,
  useChooseFreePlanMutation,
  useCreateCheckoutSessionMutation,
  useGetPlansQuery,
} from '@/store/features/billing.feature';

const TALK_TO_US_URL = 'https://calendly.com/d/d3tq-wdx-sjp';

type Decision = { kind: 'paid'; priceId: string } | { kind: 'free' } | null;

const formatDeadline = (raw: string | null | undefined) => {
  if (!raw) return null;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(parsed);
};

const checkoutWasCanceled = () => {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return [params.get('status'), params.get('checkout')].some((value) =>
    value ? ['cancelled', 'canceled'].includes(value.toLowerCase()) : false
  );
};

/**
 * Dedicated signed-in trial resolution page. TrialAccessBoundary renders this
 * instead of the application, which means no background controls, shortcuts,
 * mobile blocker, or Radix focus scope remain mounted.
 */
export function TrialExpiredGate() {
  const dispatch = useAppDispatch();
  const { trial, isReady, organizationId } = useFeatureAccess();
  const posthog = usePostHog();
  const clerk = useClerk();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const decisionRef = useRef<Decision>(null);
  const {
    beginOrganizationAction,
    isOrganizationActionCurrent,
    invalidateOrganizationActions,
  } = useOrganizationActionFence(organizationId);
  const visible = isReady && trial?.must_choose_plan === true;

  const {
    currentData: availablePlans,
    isLoading: plansLoading,
    isFetching: plansFetching,
    isError: plansError,
    refetch: refetchPlans,
  } = useGetPlansQuery(
    { organizationId },
    { skip: !visible || !organizationId }
  );
  const [createCheckoutSession] = useCreateCheckoutSessionMutation();
  const [chooseFreePlan] = useChooseFreePlanMutation();

  const [interval, setInterval] = useState<'month' | 'year'>('month');
  const v4 = availablePlans?.pricing_version === 'v4';
  useEffect(() => {
    if (v4) setInterval('year');
  }, [v4, organizationId]);
  const [decision, setDecision] = useState<Decision>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const canceled = useMemo(checkoutWasCanceled, []);

  useEffect(() => {
    decisionRef.current = null;
    setDecision(null);
    setActionError(null);
  }, [organizationId]);
  // Browser Back from Checkout: the page returns with the choice still locked.
  usePageRestored(() => {
    decisionRef.current = null;
    setDecision(null);
  });

  const viewedRef = useRef(false);
  useEffect(() => {
    if (visible && !viewedRef.current) {
      viewedRef.current = true;
      bestEffortAnalytics(() =>
        posthog?.capture('trial_paywall_viewed', {
          gate_reason: trial?.gate_reason ?? 'trial_expired',
          winback_active: trial?.winback_active ?? false,
        })
      );
    }
    if (!visible) viewedRef.current = false;
  }, [visible, posthog, trial?.gate_reason, trial?.winback_active]);

  useEffect(() => {
    if (visible) headingRef.current?.focus();
  }, [visible]);

  if (!visible) return null;

  const plans =
    availablePlans?.plans
      .filter((plan) =>
        interval === 'month' ? plan.monthly_price : plan.yearly_price
      )
      .sort(
        (a, b) =>
          (a.monthly_price?.price || a.yearly_price?.price || 0) -
          (b.monthly_price?.price || b.yearly_price?.price || 0)
      ) ?? [];
  const tierGroups = groupPlansByTier(plans);
  const currentMembers = Math.max(availablePlans?.seats_used ?? 1, 1);
  const discountDeadline = formatDeadline(trial?.winback_ends_at);
  const actionLocked = decision !== null;

  const releaseDecision = () => {
    decisionRef.current = null;
    setDecision(null);
  };

  const claimDecision = (next: Exclude<Decision, null>) => {
    // State does not update until React's next render. The ref is the
    // synchronous mutex that closes paid then Free and double-click races.
    if (decisionRef.current) return false;
    decisionRef.current = next;
    setDecision(next);
    setActionError(null);
    return true;
  };

  const handleCheckout = async (
    priceId: string,
    planDetails?: PricingPlanSelection
  ) => {
    const action = beginOrganizationAction();
    if (!action || !claimDecision({ kind: 'paid', priceId })) return;

    // Guarded, and NOT inside the try below: the decision mutex is already
    // claimed at this point, so a throwing capture that escaped would leave
    // every plan control disabled with no way to release them.
    bestEffortAnalytics(() =>
      posthog?.capture('trial_paywall_plan_selected', {
        gate_reason: trial?.gate_reason ?? 'trial_expired',
        plan_name: planDetails?.name,
        interval,
        winback_active: trial?.winback_active ?? false,
      })
    );

    try {
      const response = await createCheckoutSession({
        organizationId: action.organizationId,
        price_id: priceId,
        seats: Math.max(planDetails?.seats ?? 1, currentMembers),
        credit_addon_price_id: planDetails?.credit_addon_price_id,
        credit_addon_credits: planDetails?.credit_addon_credits,
      }).unwrap();

      if (!isOrganizationActionCurrent(action)) return;

      if (
        response.organization_id &&
        response.organization_id !== action.organizationId
      ) {
        throw new Error(
          'Checkout was stopped because the active organization changed.'
        );
      }

      if (!v4 && trial?.winback_active && response.discount_applied !== true) {
        throw new Error(
          'We could not verify your 30% offer. No checkout was opened. Please retry.'
        );
      }

      if (response.action === 'modified') {
        window.location.href = response.url || '/plans?status=success';
        return;
      }

      const redirected = await redirectToStripeCheckout(response, () =>
        isOrganizationActionCurrent(action)
      );
      if (!isOrganizationActionCurrent(action)) return;
      if (!redirected) {
        setActionError('Checkout did not open. Please retry your plan choice.');
        releaseDecision();
      }
    } catch (error) {
      if (!isOrganizationActionCurrent(action)) return;
      const message =
        (error as { data?: { detail?: string } })?.data?.detail ||
        (error as Error)?.message ||
        'Could not start checkout. Please try again.';
      setActionError(message);
      toast.error(message);
      releaseDecision();
    }
  };

  const handleChooseFree = async () => {
    const action = beginOrganizationAction();
    if (!action || !claimDecision({ kind: 'free' })) return;

    try {
      await chooseFreePlan({ organizationId: action.organizationId }).unwrap();
      if (!isOrganizationActionCurrent(action)) return;

      // Resolve the mandatory screen from the committed mutation result first.
      // The invalidated request still reconciles in the background, but a failed
      // refetch cannot strand the user behind a stale spinner.
      const patchResult = dispatch(
        billingApi.util.updateQueryData(
          'getFeatureAccess',
          { organizationId: action.organizationId },
          (access) => {
            if (!access.trial) return;
            access.trial.active = false;
            access.trial.expired = true;
            access.trial.must_choose_plan = false;
            access.trial.plan_chosen = 'free';
          }
        )
      );

      // The success path deliberately does NOT call releaseDecision() — the
      // patch above flips `must_choose_plan`, which makes the gate invisible
      // and unmounts it. That chain has one unguarded link: `updateQueryData`
      // is a silent no-op when no cache entry exists for exactly these
      // arguments. Nothing flips, the gate stays mounted, and the claim stays
      // held — leaving the user on a disabled "Switching to Free..." with no
      // error and no way out. An empty patch list is how we detect that; the
      // invalidated refetch still resolves the screen, and the controls are
      // usable again in the meantime.
      if (!patchResult?.patches?.length) {
        releaseDecision();
      }
    } catch (error) {
      if (!isOrganizationActionCurrent(action)) return;
      const message =
        (error as { data?: { detail?: string } })?.data?.detail ||
        'Could not switch to the Free plan. Please try again.';
      setActionError(message);
      toast.error(message);
      releaseDecision();
    }
  };

  const handleSignOut = () => {
    invalidateOrganizationActions();
    // Clerk can reject (offline, revoked session). Unhandled, that is a silent
    // no-op for the user and an unhandled rejection for us. `Promise.resolve`
    // wraps a signOut that returns nothing at all.
    void Promise.resolve(clerk.signOut()).catch(() => {
      const message = 'Could not sign you out. Please try again.';
      setActionError(message);
      toast.error(message);
    });
  };

  return (
    <main
      className='min-h-dvh overflow-y-auto bg-background'
      aria-labelledby='trial-expired-title'
      aria-busy={actionLocked}
    >
      <div className='mx-auto flex min-h-dvh w-full max-w-6xl flex-col px-3 py-5 sm:px-6 sm:py-8 lg:px-8'>
        <header className='flex min-w-0 flex-col items-start justify-between gap-3 sm:flex-row'>
          <div>
            <h1
              id='trial-expired-title'
              ref={headingRef}
              tabIndex={-1}
              className='text-xl font-bold tracking-tight text-foreground outline-hidden sm:text-2xl'
            >
              Your 14-day {trial?.plan === 'pro' ? 'Pro' : 'Enterprise'} trial
              has ended
            </h1>
            <p className='mt-2 max-w-2xl text-sm leading-6 text-muted-foreground'>
              Your lists, enrichments, and integrations are saved. Choose a plan
              or continue on Free to return to your workspace.
            </p>
          </div>
          <div className='flex shrink-0 items-center gap-2'>
            <OrganizationSwitcher hidePersonal />
            <Button
              variant='ghost'
              size='sm'
              className='text-xs text-muted-foreground'
              onClick={handleSignOut}
            >
              Sign out
            </Button>
          </div>
        </header>

        {canceled && (
          <div
            role='status'
            className='mt-4 rounded-xl border border-sky-700/30 bg-sky-50 px-4 py-3 text-sm text-sky-950 dark:border-sky-400/40 dark:bg-sky-950/50 dark:text-sky-100'
          >
            Checkout was canceled. You were not charged. Your Free refill is
            preserved while you choose another plan or continue on Free.
          </div>
        )}

        {/* v4 checkout applies no win-back coupon, so it is not offered. */}
        {!v4 && trial?.winback_active && (
          <div className='mt-4 rounded-xl border border-emerald-700/30 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-950 dark:border-emerald-400/40 dark:bg-emerald-950/50 dark:text-emerald-100'>
            30% off your first year on any plan. Checkout opens only after the
            discount is verified.
            {discountDeadline ? ` Offer ends ${discountDeadline}.` : ''}
          </div>
        )}

        {actionError && (
          <div
            role='alert'
            className='mt-4 rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive'
          >
            {actionError}
          </div>
        )}

        <section
          aria-labelledby='free-plan-title'
          className='mt-5 rounded-xl border border-border bg-muted/20 p-4'
        >
          <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
            <div>
              <h2
                id='free-plan-title'
                className='text-sm font-semibold text-foreground'
              >
                Continue on Free
              </h2>
              <p className='mt-1 text-xs leading-5 text-muted-foreground'>
                {v4
                  ? '50 credits each month, CSV import and export, and 3 included seats.'
                  : '30 credits each month, CSV upload, 100 leads per list, and 1 included seat.'}{' '}
                Unused trial credits are removed. Your {currentMembers} current
                member
                {currentMembers === 1 ? ' stays' : 's stay'} in the
                organization. New invitations are blocked while membership is
                above the Free allowance.
              </p>
            </div>
            <Button
              variant='outline'
              className='min-w-40 shrink-0'
              disabled={actionLocked}
              onClick={handleChooseFree}
            >
              {decision?.kind === 'free' && (
                <AppIcon
                  icon={iconSpinnerThird}
                  className='mr-2 motion-safe:animate-spin'
                  style={{ fontSize: 14 }}
                  aria-hidden='true'
                />
              )}
              {decision?.kind === 'free'
                ? 'Switching to Free...'
                : 'Continue on Free'}
            </Button>
          </div>
        </section>

        <div className='mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
          <div>
            <h2 className='text-base font-semibold text-foreground'>
              Choose a paid plan
            </h2>
            <p className='mt-0.5 text-xs text-muted-foreground'>
              Your organization has {currentMembers} current member
              {currentMembers === 1 ? '' : 's'}. Checkout includes at least that
              many seats.
            </p>
          </div>
          <div className='flex flex-wrap items-center gap-2'>
            <div
              role='group'
              aria-label='Billing period'
              className='inline-flex items-center rounded-full border border-border bg-muted/40 p-0.5'
            >
              {(['month', 'year'] as const).map((value) => (
                <button
                  key={value}
                  type='button'
                  aria-pressed={interval === value}
                  disabled={actionLocked}
                  onClick={() => setInterval(value)}
                  className={
                    interval === value
                      ? 'rounded-full bg-blue-700 px-4 py-1.5 text-xs font-semibold text-white disabled:opacity-60 dark:bg-blue-500'
                      : 'rounded-full px-4 py-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground disabled:opacity-60 dark:text-white/70 dark:hover:text-white'
                  }
                >
                  {value === 'month' ? 'Monthly' : 'Yearly'}
                </button>
              ))}
            </div>
            <span className='rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-900 dark:bg-emerald-900/50 dark:text-emerald-100'>
              Save 25% yearly
            </span>
          </div>
        </div>

        {plansLoading || (plansFetching && !availablePlans) ? (
          <div
            role='status'
            aria-live='polite'
            className='flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground'
          >
            <AppIcon
              icon={iconSpinnerThird}
              className='motion-safe:animate-spin'
              style={{ fontSize: 20 }}
              aria-hidden='true'
            />
            Loading paid plans...
          </div>
        ) : plansError || plans.length === 0 ? (
          <div
            role='alert'
            aria-labelledby='trial-paid-plan-catalog-alert-title'
            className='mt-5 rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-5 text-center wrap-break-word forced-colors:border-[Highlight]'
          >
            <p
              id='trial-paid-plan-catalog-alert-title'
              className='text-sm font-semibold text-destructive forced-colors:text-[CanvasText]'
            >
              {plansError
                ? 'Paid plans could not be loaded.'
                : 'No paid plans are available right now.'}
            </p>
            <p className='mt-1 text-xs text-muted-foreground'>
              Retry the plan catalog, continue on Free, or sign out.
            </p>
            <Button
              variant='outline'
              size='sm'
              className='mt-3'
              disabled={plansFetching || actionLocked}
              onClick={() => void refetchPlans()}
            >
              {plansFetching ? 'Retrying...' : 'Retry paid plans'}
            </Button>
          </div>
        ) : v4 && availablePlans ? (
          <div className='mt-5'>
            <PricingV4Cards
              organizationId={organizationId}
              catalog={availablePlans}
              interval={interval}
              disabled={actionLocked}
              processingPriceId={
                decision?.kind === 'paid' ? decision.priceId : null
              }
              onCheckout={handleCheckout}
            />
          </div>
        ) : (
          <div className='mt-5 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3'>
            {tierGroups.map((group) => (
              <PlanTierCard
                key={group.category}
                group={group}
                interval={interval}
                processingPriceId={
                  decision?.kind === 'paid' ? decision.priceId : null
                }
                onCheckout={handleCheckout}
                extraSeat={availablePlans?.extra_seat ?? null}
                disabled={actionLocked}
                minimumSeats={currentMembers}
                discountPercent={trial?.winback_active ? 30 : undefined}
                showSeatManagementLink={false}
              />
            ))}
          </div>
        )}

        <p className='mt-6 pb-2 text-center text-xs text-muted-foreground'>
          Need help choosing?{' '}
          <a
            href={TALK_TO_US_URL}
            target='_blank'
            rel='noreferrer'
            className='font-medium text-primary underline underline-offset-2'
          >
            Talk to us
          </a>
          .
        </p>
      </div>
    </main>
  );
}
